import React from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const TOOLBAR_OPTIONS = [
  [{ header: [2, 3, false] }],
  ['bold', 'italic'],
  ['blockquote'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['link'],
  ['clean'],
];

// Uncontrolled by design — Quill owns its own DOM/content. `defaultValue` seeds it once on
// mount; pass a `key` from the caller to remount (and reseed) when switching documents.
export function QuillEditor({ defaultValue = '', placeholder, onChange, className }) {
  const containerRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  React.useEffect(() => {
    const container = containerRef.current;
    const quill = new Quill(container, {
      theme: 'snow',
      placeholder,
      modules: { toolbar: TOOLBAR_OPTIONS },
    });
    if (defaultValue) quill.clipboard.dangerouslyPasteHTML(defaultValue);
    quill.on('text-change', () => onChangeRef.current?.(quill.root.innerHTML));

    // StrictMode runs this effect, cleans it up, then runs it again on the same DOM node
    // (no real unmount in between) — so cleanup has to fully undo what Quill added, or the
    // second run creates a second toolbar/editor stacked on top of the first.
    return () => {
      quill.getModule('toolbar')?.container?.remove();
      container.innerHTML = '';
      container.classList.remove('ql-container', 'ql-snow');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quill inserts the toolbar it builds from TOOLBAR_OPTIONS as a sibling of the container
  // it's given, not a child — nesting the container one level down keeps both the toolbar
  // and the editor inside this wrapper, so `className` styling reaches both and React cleans
  // up the whole thing (toolbar included) when this component unmounts.
  return (
    <div className={className}>
      <div ref={containerRef} />
    </div>
  );
}

export default QuillEditor;
